import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import { motion } from 'motion/react';
import { appointmentsService } from '../../services/api';
import { AppointmentTracking, AppointmentStatus } from '../../types';
import ManageBookingCard from '../../components/ManageBookingCard';
import { format, parseISO } from 'date-fns';
import { shopWallClockDate, shopZoneAbbreviation } from '../../utils/shopTime';
import { AnimatedText, Reveal, Stagger, StaggerItem } from '../../motion';
import { fadeUp, staggerItemTight } from '../../motion/variants';
import { StatusChip, EmptyState } from '../../components/ui';
import { duration, ease, stagger } from '../../theme/tokens';
import { canvasAccentText } from '../../theme';
import { useMotionSafe } from '../../motion/useMotionSafe';

/** The happy path a repair travels through, in order. */
const JOURNEY: { status: AppointmentStatus; label: string; blurb: string }[] = [
  { status: AppointmentStatus.PENDING, label: 'Requested', blurb: 'We received your booking' },
  { status: AppointmentStatus.CONFIRMED, label: 'Confirmed', blurb: 'Your slot is locked in' },
  { status: AppointmentStatus.IN_PROGRESS, label: 'In the workshop', blurb: 'A technician is on it' },
  { status: AppointmentStatus.COMPLETED, label: 'Work complete', blurb: 'All tasks finished' },
  { status: AppointmentStatus.READY_FOR_PICKUP, label: 'Ready for pickup', blurb: 'Come collect your vehicle' },
];

/** Mirrors CUSTOMER_EDITABLE on the API — the card is hidden once work starts. */
const CHANGEABLE_STATUSES: AppointmentStatus[] = [
  AppointmentStatus.PENDING,
  AppointmentStatus.CONFIRMED,
];

const AppointmentTrackingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // Present only on links from the confirmation and reminder emails; it is what
  // authorizes changing the booking rather than merely reading it.
  const [searchParams] = useSearchParams();
  const manageToken = searchParams.get('t');
  const [appointment, setAppointment] = useState<AppointmentTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { enabled: motionEnabled } = useMotionSafe();

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!id) {
        setError('Invalid tracking ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await appointmentsService.getAppointmentTracking(id);
        setAppointment(data);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [id]);

  // On the shop's clock, with its zone named, so a customer reading from
  // another state sees the time the shop will actually expect them.
  const formatDateTime = (dateTimeValue?: number | string) => {
    if (!dateTimeValue) return 'Not scheduled';
    try {
      if (typeof dateTimeValue === 'number') {
        const zone = appointment?.timezone;
        const label = shopZoneAbbreviation(zone, new Date(dateTimeValue * 1000));
        return `${format(shopWallClockDate(dateTimeValue, zone), 'EEEE, MMMM d, yyyy · h:mm a')}${label ? ` ${label}` : ''}`;
      }
      return format(parseISO(dateTimeValue), 'EEEE, MMMM d, yyyy · h:mm a');
    } catch {
      return String(dateTimeValue);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Skeleton variant="text" width="45%" height={44} />
        <Skeleton variant="text" width="65%" sx={{ mb: 4 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={280} />
      </Container>
    );
  }

  if (error || !appointment) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Appointment not found'}
        </Alert>
        <EmptyState
          title="We couldn't find that appointment"
          description="Check the link in your confirmation email, or contact the shop directly."
        />
      </Container>
    );
  }

  const isCancelled = appointment.status === AppointmentStatus.CANCELLED;
  const currentIndex = JOURNEY.findIndex((step) => step.status === appointment.status);
  // CLOSED_OUT sits past the end of the journey — treat it as fully complete.
  const effectiveIndex =
    appointment.status === AppointmentStatus.CLOSED_OUT ? JOURNEY.length - 1 : currentIndex;

  const details = [
    { icon: <EventOutlinedIcon />, label: 'Appointment', value: formatDateTime(appointment.appointmentDateTime) },
    {
      icon: <DirectionsCarOutlinedIcon />,
      label: 'Vehicle',
      value: `${appointment.vehicleModel}${appointment.vehicleYear ? ` (${appointment.vehicleYear})` : ''}`,
    },
    {
      icon: <StorefrontOutlinedIcon />,
      label: 'Shop',
      value: appointment.shop ? appointment.shop.name : 'Not assigned',
      sub: appointment.shop?.address,
    },
    ...(appointment.mechanic
      ? [
          {
            icon: <EngineeringOutlinedIcon />,
            label: 'Your technician',
            value: appointment.mechanic.name,
            sub: appointment.mechanic.specialization ?? undefined,
          },
        ]
      : []),
  ];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="overline" sx={{ color: canvasAccentText }}>
          Repair tracking
        </Typography>
        <AnimatedText variant="h3" component="h1" text={`Hi ${appointment.customerName.split(' ')[0]}, here's where things stand.`} />
      </Stack>

      {/* Progress journey */}
      <Reveal variants={fadeUp}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 3 }}
              spacing={2}
            >
              <Typography variant="h6">Status</Typography>
              <StatusChip status={appointment.status} />
            </Stack>

            {isCancelled ? (
              <Alert severity="error" variant="outlined">
                This appointment was canceled. Contact the shop if that looks wrong.
              </Alert>
            ) : (
              <Stack spacing={0}>
                {JOURNEY.map((step, index) => {
                  const done = index <= effectiveIndex;
                  const isCurrent = index === effectiveIndex;
                  const isLast = index === JOURNEY.length - 1;

                  return (
                    <Stack key={step.status} direction="row" spacing={2} alignItems="flex-start">
                      {/* Rail */}
                      <Stack alignItems="center" sx={{ alignSelf: 'stretch' }}>
                        <Box
                          component={motionEnabled ? motion.div : 'div'}
                          {...(motionEnabled
                            ? {
                                initial: { scale: 0.6, opacity: 0 },
                                animate: { scale: 1, opacity: 1 },
                                transition: { delay: index * 0.09, duration: duration.base, ease: ease.out },
                              }
                            : {})}
                          sx={{
                            display: 'grid',
                            placeItems: 'center',
                            color: done ? 'primary.main' : 'text.disabled',
                            '& svg': { fontSize: 24 },
                          }}
                        >
                          {done ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                        </Box>

                        {!isLast && (
                          <Box
                            sx={{
                              width: 2,
                              flexGrow: 1,
                              minHeight: 34,
                              my: 0.5,
                              borderRadius: 1,
                              backgroundColor: (t) =>
                                index < effectiveIndex
                                  ? t.palette.primary.main
                                  : alpha(t.palette.text.primary, 0.12),
                              transition: 'background-color 300ms',
                            }}
                          />
                        )}
                      </Stack>

                      <Box sx={{ pb: isLast ? 0 : 2.5, pt: 0.25 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: done ? 'text.primary' : 'text.disabled', fontWeight: isCurrent ? 700 : 600 }}
                        >
                          {step.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {step.blurb}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Reveal>

      {/* Details */}
      <Reveal variants={fadeUp} delay={0.1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2.5 }}>
              Details
            </Typography>
            <Stack spacing={2.5}>
              {details.map((detail) => (
                <Stack key={detail.label} direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ color: 'text.secondary', mt: 0.25, '& svg': { fontSize: 20 } }}>{detail.icon}</Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {detail.label}
                    </Typography>
                    <Typography variant="body1">{detail.value}</Typography>
                    {detail.sub && (
                      <Typography variant="body2" color="text.secondary">
                        {detail.sub}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="caption" color="text.secondary" display="block">
              Requested service
            </Typography>
            <Typography variant="body1">{appointment.serviceDescription}</Typography>

            {appointment.comments && (
              <>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Notes
                </Typography>
                <Typography variant="body2">{appointment.comments}</Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Reveal>

      {/* Tasks */}
      {appointment.tasks.length > 0 && (
        <Reveal variants={fadeUp} delay={0.15}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Service tasks
              </Typography>

              <Stagger interval={stagger.tight}>
                <Stack spacing={1}>
                  {appointment.tasks.map((task) => (
                    <StaggerItem key={task.id} variants={staggerItemTight}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Typography variant="body2">{task.title}</Typography>
                        <StatusChip status={task.status} />
                      </Stack>
                    </StaggerItem>
                  ))}
                </Stack>
              </Stagger>
            </CardContent>
          </Card>
        </Reveal>
      )}

      {manageToken && CHANGEABLE_STATUSES.includes(appointment.status) && (
        <ManageBookingCard
          appointment={appointment}
          token={manageToken}
          shopId={appointment.shopId}
          onChanged={setAppointment}
        />
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3, textAlign: 'center' }}>
        Save this page to check back on your repair at any time.
      </Typography>
    </Container>
  );
};

export default AppointmentTrackingPage;
